import React from 'react';
import { publicAsset } from '@/utils/publicAsset';

/**
 * Pam is the brand's moat — a real Black LCSW who reviewed every question herself.
 * The blog is HER column, so her face + voice appear on the index masthead, on
 * every post header, and in the hand-signed sign-off (echoing the email letters).
 * These pieces are the shared source of truth for that presence.
 */

const PAM_PHOTO = publicAsset('pamela-image.png');

export const PAM_NAME = 'Pamela Coin, LCSW';
export const PAM_ROLE = 'Co-founder & Board Approved Clinical Supervisor';
export const PAM_BIO = 'Licensed clinical social worker. I reviewed every question myself.';

/** Posts authored by Pam get her signature; team posts sign off differently. */
export const isPamAuthored = (author?: string): boolean =>
  !!author && /pam/i.test(author);

type AvatarSize = 'sm' | 'md' | 'lg';

const AVATAR_SIZES: Record<AvatarSize, string> = {
  sm: 'w-11 h-11',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
};

/** Intrinsic px per size (tailwind unit = 4px) — reserves layout box, prevents CLS. */
const AVATAR_PX: Record<AvatarSize, number> = {
  sm: 44,
  md: 56,
  lg: 80,
};

interface PamAvatarProps {
  size?: AvatarSize;
  className?: string;
}

/** Pam's real headshot with a hard ledge — never a hotlinked or generic image. */
export const PamAvatar: React.FC<PamAvatarProps> = ({ size = 'md', className = '' }) => (
  <img
    src={PAM_PHOTO}
    alt={PAM_NAME}
    loading="lazy"
    decoding="async"
    width={AVATAR_PX[size]}
    height={AVATAR_PX[size]}
    className={`${AVATAR_SIZES[size]} shrink-0 rounded-full object-cover border-2 border-primary-200 shadow-ledge-sm ${className}`}
  />
);

/** Fallback monogram for non-Pam (team) authors — same ledge treatment. */
const TeamMonogram: React.FC = () => (
  <div className="w-14 h-14 shrink-0 rounded-full bg-primary-100 border-2 border-primary-200 shadow-ledge-sm flex items-center justify-center">
    <span aria-hidden="true" className="font-display font-bold text-primary-700 text-lg">SW</span>
  </div>
);

/**
 * The masthead byline for the column index — full presence: face, name, and the
 * "I reviewed every question myself" line that makes the blog feel like a person.
 */
export const PamColumnByline: React.FC = () => (
  <div className="inline-flex items-center gap-4 text-left">
    <PamAvatar size="lg" />
    <div>
      <p className="font-display font-semibold text-lg text-neutral-900">{PAM_NAME}</p>
      <p className="text-sm text-neutral-600 leading-snug max-w-xs">{PAM_BIO}</p>
    </div>
  </div>
);

interface PamPostBylineProps {
  author: string;
}

/** Compact author row for a post header. Pam gets her face; the team gets its mark. */
export const PamPostByline: React.FC<PamPostBylineProps> = ({ author }) => {
  const pam = isPamAuthored(author);

  return (
    <div className="flex items-center gap-3">
      {pam ? <PamAvatar size="md" /> : <TeamMonogram />}
      <div>
        <p className="font-display font-semibold text-base text-neutral-900">
          {pam ? PAM_NAME : author}
        </p>
        <p className="text-xs text-neutral-600">
          {pam ? PAM_ROLE : 'Social Work Test Prep Academy'}
        </p>
      </div>
    </div>
  );
};

interface PamSignoffProps {
  author: string;
}

/**
 * The closing hand-signed sign-off — the warmth of the email letters. Pam signs
 * "— Pam"; team posts close with the academy's name so the voice stays honest.
 */
export const PamSignoff: React.FC<PamSignoffProps> = ({ author }) => {
  const pam = isPamAuthored(author);

  return (
    <div className="mt-12 flex items-center gap-4 border-t-2 border-neutral-200 pt-8">
      {pam ? <PamAvatar size="md" /> : <TeamMonogram />}
      <div>
        <p className="font-display text-2xl font-semibold text-primary-700">
          {pam ? '— Pam' : '— The SWTPA Team'}
        </p>
        <p className="text-sm text-neutral-600">
          {pam ? `${PAM_NAME} · ${PAM_BIO}` : 'Social Work Test Prep Academy'}
        </p>
      </div>
    </div>
  );
};
